import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { StyleSheet, css } from 'aphrodite';
import { Theme } from '../../config/theme';
import ErrorContainer from '../ErrorContainer';

export default class TextField extends Component {
  constructor(props) {
    super(props);
    this.assignClasses = this.assignClasses.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.onKeyDown = this.props.onKeyDown.bind(this);

    this.state = {
      error: this.props.error,
      remaining: this.props.limit,
    };

    this.styles = StyleSheet.create({
      field: {
        width: '50%',
        marginBottom: 15,
        padding: '0 5px',

        [Theme.mobile_query]: {
          width: '100%',
        },
      },
      field_fw: {
        width: '100%',
      },
      remaining: {
        marginTop: 8,
        textAlign: 'right',
        color: '#828282',
        fontSize: 14,
        fontFamily: Theme.fontFamily,
      },
      label: {
        display: 'block',
        marginBottom: 9,
        fontFamily: Theme.fontFamily,
        fontSize: 14,
        color: '#828282',
      },
      label_error: {
        color: '#E85E5E',
      },
      input: {
        width: '100%',
        height: 50,
        padding: '0 16px',
        fontFamily: Theme.fontFamily,
        fontSize: 15,
        color: '#000e1f',
        borderRadius: 3,
        backgroundColor: '#fff',
        border: '1px solid #e4e4e4',
        outline: 'none',
        transition: 'all 300ms',
        boxSizing: 'border-box',

        [Theme.mobile_query]: {
          fontSize: 16,
        },

        '::placeholder': {
          fontSize: 14,
          color: '#bbb',
        },

        ':focus': {
          borderColor: Theme.turquoise,
        },
      },
      input_disabled: {
        backgroundColor: Theme.light_grey,
        color: Theme.grey,
      },
      input_error: {
        color: '#E85E5E',
        borderColor: '#E85E5E',
        borderWidth: 2,
        borderRadius: '3px 3px 0 0',

        '::placeholder': {
          color: '#E85E5E',
        },

        ':focus': {
          borderColor: '#E85E5E',
        },
      },
      textarea: {
        display: 'block',
        height: this.props.height ? this.props.height : 114,
        padding: 16,
        resize: 'none',
      },
    });
  }

  componentDidMount() {
    if (this.props.focus) {
      this.inputField.focus();
    }
  }

  componentDidUpdate() {
    if (this.props.error !== this.state.error) {
      setTimeout(() => {
        this.setState({
          error: this.props.error,
        });
      }, 200);
    }
  }

  onKeyDown(event) {
    this.props.onKeyDown(event);
  }

  handleChange(event) {
    if (this.props.required && event.target.value.length === 0) {
      this.setState({
        error: 'This field is required',
      });
    }

    if (this.props.handler && typeof this.props.handler === 'function') {
      this.props.handler(this.props.name, event.target.value);
    }
  }

  assignClasses() {
    this.fieldClasses = [
      this.styles.field,
      this.props.fullWidth ? this.styles.field_fw : null,
    ];

    this.labelClasses = [
      this.styles.label,
      this.state.error ? this.styles.label_error : null,
    ];

    this.inputClasses = [
      this.styles.input,
      this.state.error ? this.styles.input_error : null,
      this.props.disabled ? this.styles.input_disabled : null,
    ];

    this.textAreaClasses = [
      this.inputClasses,
      this.styles.textarea,
    ];
  }

  render() {
    this.assignClasses();

    return (
      <div className={css(this.fieldClasses, this.props.styles)}>
        {this.props.label ?
          <label htmlFor={this.props.id} className={css(this.labelClasses)}>
            {this.props.label}
          </label>
        : null}
        {this.props.textArea ?
          <textarea
            id={this.props.id}
            name={this.props.name}
            ref={(input) => { this.inputField = input; }}
            className={css(this.textAreaClasses)}
            defaultValue={this.props.value}
            value={this.props.value}
            placeholder={this.props.placeholder}
            maxLength={this.props.limit}
            height={this.props.height}
            onChange={this.handleChange}
          />
        : null}
        {!this.props.creditCardNum && !this.props.textArea && !this.props.creditCardExp && !this.props.creditCardCVC ? // eslint-disable-line
          <input
            ref={(input) => { this.inputField = input; }}
            id={this.props.id}
            disabled={this.props.disabled}
            type={this.props.email ? 'email' : this.props.type}
            name={this.props.name}
            defaultValue={this.props.value}
            value={this.props.value}
            onKeyDown={this.onKeyDown}
            className={css(this.inputClasses)}
            placeholder={this.props.placeholder}
            onChange={this.handleChange}
          />
        : null}
        {this.state.error ?
          <ErrorContainer message={this.state.error} />
        : null}
        {this.props.showRemaining && this.props.limit && this.props.value ?
          <div className={css(this.styles.remaining)}>
            {this.props.value.length} / {this.props.limit}
          </div>
        : null}
      </div>
    );
  }
}

TextField.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  type: PropTypes.string,
  height: PropTypes.number,
  limit: PropTypes.number,
  textArea: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  email: PropTypes.bool,
  fullWidth: PropTypes.bool,
  showRemaining: PropTypes.bool,
  creditCardNum: PropTypes.bool,
  creditCardExp: PropTypes.bool,
  creditCardCVC: PropTypes.bool,
  focus: PropTypes.bool,
  styles: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.object,
  ])),
  handler: PropTypes.func,
  onKeyDown: PropTypes.func,
};

TextField.defaultProps = {
  styles: null,
  label: null,
  disabled: false,
  placeholder: null,
  error: null,
  type: 'text',
  focus: false,
  textArea: false,
  email: false,
  limit: null,
  required: false,
  height: null,
  fullWidth: false,
  showRemaining: false,
  creditCardNum: false,
  creditCardExp: false,
  creditCardCVC: false,
  handler: () => {},
  onKeyDown: () => {},
};



// WEBPACK FOOTER //
// ./src/components/TextField/index.js